import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useUpdateUserLanguageMutation } from '@/shared/hooks/queries/generated';
import type { SupportedLanguage } from '@/shared/i18n';
import { LOCALIZED_TITLE_QUERY_KEYS } from '@/shared/i18n/localizedTitleQueries';

// Marque un choix de langue EXPLICITE (sélecteur UI) — distinct de la clé
// `language` posée par i18next-browser-languagedetector, qui persiste aussi
// une détection automatique. Sa présence prime sur la détection navigateur
// aux chargements suivants (décision produit, chantier dictionnaire
// bilingue, cf docs/superpowers/api-changelog/2026-09-10-langue-utilisateur.md).
const EXPLICIT_LANGUAGE_STORAGE_KEY = 'recop.language.explicit';

const isSupportedLanguage = (value: string | null): value is SupportedLanguage => value === 'fr' || value === 'en';

const readExplicitLanguage = (): SupportedLanguage | null => {
  const stored = localStorage.getItem(EXPLICIT_LANGUAGE_STORAGE_KEY);
  return isSupportedLanguage(stored) ? stored : null;
};

// 'fr' si le navigateur annonce une variante française, 'en' sinon (toute
// autre langue ⇒ anglais) — deux langues seulement pour l'instant.
const detectBrowserLanguage = (): SupportedLanguage => (navigator.language?.startsWith('fr') ? 'fr' : 'en');

const invalidateLocalizedTitleQueries = (queryClient: QueryClient) =>
  Promise.all(LOCALIZED_TITLE_QUERY_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey })));

/**
 * Langue active du compte + choix explicite depuis l'UI (sélecteur de
 * langue). Le choix explicite est mémorisé côté front ET poussé au serveur
 * (jamais pour un utilisateur non authentifié), puis les titres dictionnaire
 * déjà en cache sont invalidés pour un effet immédiat.
 */
export const useUserLanguage = () => {
  const { i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const updateUserLanguageMutation = useUpdateUserLanguageMutation();

  const language: SupportedLanguage = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  const setLanguage = useCallback(
    (nextLanguage: SupportedLanguage) => {
      localStorage.setItem(EXPLICIT_LANGUAGE_STORAGE_KEY, nextLanguage);
      void i18n.changeLanguage(nextLanguage);

      // Jamais de mutation pour un utilisateur non authentifié.
      if (!isAuthenticated) return;

      updateUserLanguageMutation.mutate(
        { language: nextLanguage },
        { onSuccess: () => void invalidateLocalizedTitleQueries(queryClient) },
      );
    },
    [i18n, isAuthenticated, queryClient, updateUserLanguageMutation],
  );

  return { language, setLanguage, isUpdating: updateUserLanguageMutation.isPending };
};

/**
 * Setup de langue au chargement d'une session utilisateur AUTHENTIFIÉE — à
 * monter une seule fois, à la racine de l'app (cf `App.tsx`). La langue de
 * l'ouverture de session est le choix EXPLICITE mémorisé s'il existe, la
 * détection NAVIGATEUR sinon (décision propriétaire, verbatim, chantier
 * dictionnaire bilingue) — et elle est dans les DEUX cas appliquée localement
 * ET poussée au serveur.
 *
 * ⚠️⚠️ **Le choix explicite est POUSSÉ, pas seulement appliqué localement.**
 * Ce hook sortait sans rien faire dès qu'un choix explicite existait : sur un
 * appareil ayant choisi `en`, une ouverture de session depuis un AUTRE appareil
 * (qui, lui, pousse sa détection navigateur `fr`) laissait le serveur en `fr`
 * pendant que l'UI locale restait en `en` — les titres dictionnaire arrivaient
 * donc en français dans une interface anglaise, sans erreur nulle part. La
 * mutation est IDEMPOTENTE (elle écrit la valeur, elle ne bascule rien) : la
 * repousser à chaque ouverture de session ne coûte qu'un aller-retour.
 *
 * ⚠️ **Résiduel v1 assumé** : deux appareils dont l'un n'a jamais choisi
 * peuvent encore se disputer la valeur serveur (le passif pousse sa détection
 * navigateur à chaque ouverture de session). Le fermer exige un état « jamais
 * choisi » côté serveur, hors de cette vague.
 */
export const useUserLanguageSetup = () => {
  const { i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const updateUserLanguageMutation = useUpdateUserLanguageMutation();
  // Détecte la transition non-authentifié → authentifié (chargement de
  // session), pas seulement le premier montage du composant.
  const wasAuthenticated = useRef(false);

  useEffect(() => {
    const justAuthenticated = isAuthenticated && !wasAuthenticated.current;
    wasAuthenticated.current = isAuthenticated;
    if (!justAuthenticated) return;

    // Le choix explicite prime sur la détection navigateur — mais il est poussé
    // comme elle : sans ça, le serveur garderait la langue du dernier appareil
    // s'étant contenté de sa détection (cf le docblock ci-dessus).
    const language = readExplicitLanguage() ?? detectBrowserLanguage();
    void i18n.changeLanguage(language);
    updateUserLanguageMutation.mutate(
      { language },
      { onSuccess: () => void invalidateLocalizedTitleQueries(queryClient) },
    );
  }, [isAuthenticated, i18n, queryClient, updateUserLanguageMutation]);
};
